import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FlaskConical, Phone } from 'lucide-react';
import type { WorkflowTestInputs } from '@vagusx/clients';
import { BUILDER_TEST_PATIENT } from '@vagusx/shared';
import { CallEnded } from '@/common/phone/CallEnded';
import { CallPanel } from '@/common/phone/CallPanel';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { SheetActions, SheetBody, SheetCancel } from '@/components/custom/sheet-layout';
import { getWorkflowDefnsClient } from '@/lib/api-clients';
import { useWorkflowTestInputs } from '@/hooks/use-workflows';
import { TestInputsForm } from './TestInputsForm';
import { TestReportPanel } from './TestReport';
import { TestTicket } from './TestTicket';

/**
 * A test call on the draft, in the browser (ADR-275). You speak as the
 * builder's fictional test patient; the agent runs the draft exactly as it
 * stood when Test was pressed — not the live version.
 *
 * A STEP test wraps the one step: a line saying what is being tested, the
 * caller's identity stood in as the test patient (not for a step that opens
 * a call or asks who is calling — that step asks, as written), the step
 * (without its conditions), and a closing line.
 *
 * THE STEP RUNS AS IT WOULD ON A CALL, AND CHANGES NOTHING (ADR-306). Every
 * booking, cancellation, text and transfer is recorded instead of made, and
 * the sheet shows what the call understood and would have done once it ends.
 * A step that needs details a real call gets from its task — a provider, an
 * office, an appointment the patient already has — asks for them first.
 *
 * When something went wrong, the tester can copy the whole call for a ticket:
 * transcript, the worker's trace of each step, the report and the definition
 * that ran (ADR-309, `TestTicket`).
 *
 * The open test is in the URL, so it can be bookmarked or sent (`search.ts`).
 * Opened from a link, it waits for Start (or for the step's inputs) rather
 * than dialing on load.
 */

export interface TestTarget {
  workflowId: string;
  workflowName: string;
  /** Set for a step test. */
  step?: {
    nodeId: string;
    name: string;
    /** False when the step asks who is calling itself (`stepTestStandsInIdentity`). */
    standsInIdentity: boolean;
  };
}

const DOB = new Date(`${BUILDER_TEST_PATIENT.dateOfBirth}T12:00:00`).toLocaleDateString(undefined, {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

export function TestCallSheet({
  target,
  autoStart,
  onClose,
}: {
  target: TestTarget | null;
  /** Dial as soon as the test opens: Test was pressed. False for a link, a reload or Back. */
  autoStart: boolean;
  onClose: () => void;
}): React.ReactElement {
  const patient = `${BUILDER_TEST_PATIENT.firstName} ${BUILDER_TEST_PATIENT.lastName}`;
  return (
    <Sheet open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        {target && (
          <>
            <SheetHeader className="border-b">
              <SheetTitle className="flex items-center gap-2">
                <FlaskConical className="h-4 w-4" />
                {target.step ? `Test step: ${target.step.name}` : `Test: ${target.workflowName}`}
              </SheetTitle>
              <SheetDescription>
                Runs the draft, not the live version. Speak as the test patient{' '}
                <span className="font-medium text-foreground">{patient}</span>, born {DOB}.
                {target.step?.standsInIdentity
                  ? ' Identity is taken as already verified, so the call goes straight to the step.'
                  : ' Identity questions run as written, and only these details are recognised: any other name or date of birth is not found, and no real chart is searched.'}
              </SheetDescription>
            </SheetHeader>
            {/* Mounted per opening, so every opening is a fresh test. */}
            <TestRun key={target.step?.nodeId ?? ''} target={target} autoStart={autoStart} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * One opening of a test. `attempt` is the test as the tester sees it: what it
 * asks for is fetched afresh for each (`useWorkflowTestInputs`), and "Start a
 * new call" makes a new one. `callKey` is the call, `null` until started; a
 * new key is a new call.
 */
interface Run {
  attempt: number;
  callKey: number | null;
  picks: WorkflowTestInputs | null;
}

function TestRun({
  target,
  autoStart,
}: {
  target: TestTarget;
  autoStart: boolean;
}): React.ReactElement {
  const [run, setRun] = useState<Run>(() => {
    const now = Date.now();
    return { attempt: now, callKey: autoStart ? now : null, picks: null };
  });
  // Which call ended, and its interaction — kept so the tester can read the
  // transcript of the call they just made.
  const [ended, setEnded] = useState<{ key: number; interactionId: string | null } | null>(null);
  const endedHere = ended && ended.key === run.callKey ? ended : null;
  // What this test asks for, from the API: it can depend on the step's config.
  const asked = useWorkflowTestInputs(
    target.workflowId,
    target.step?.nodeId ?? null,
    run.attempt,
    true
  );
  const inputs = asked.data?.inputs ?? [];
  const navigate = useNavigate();

  const start = (): void => setRun((r) => ({ ...r, callKey: Date.now() }));
  /** The inputs form's Start: on the call already open, or as a new one (a link). */
  const startWith = (picks: WorkflowTestInputs): void =>
    setRun((r) => ({ ...r, callKey: r.callKey ?? Date.now(), picks }));
  const startNew = (): void => {
    const now = Date.now();
    setRun({ attempt: now, callKey: now, picks: null });
  };
  const callPanel = (key: number, picks: WorkflowTestInputs | null): React.ReactElement => (
    <div className="min-h-0 flex-1">
      <CallPanel
        key={key}
        startCall={() =>
          getWorkflowDefnsClient().test(target.workflowId, {
            ...(target.step ? { nodeId: target.step.nodeId } : {}),
            ...(picks && Object.keys(picks).length > 0 ? { inputs: picks } : {}),
          })
        }
        banner={null}
        onEnd={(interactionId) => setEnded({ key, interactionId })}
      />
    </div>
  );

  if (endedHere) {
    return (
      <SheetBody className="px-0 pb-0">
        <CallEnded
          interactionId={endedHere.interactionId}
          onStartNew={startNew}
          onViewDetail={(interactionId) =>
            void navigate({ to: '/calls', search: { call: interactionId } })
          }
        />
        {endedHere.interactionId && (
          <>
            <TestReportPanel
              workflowId={target.workflowId}
              interactionId={endedHere.interactionId}
            />
            <TestTicket workflowId={target.workflowId} interactionId={endedHere.interactionId} />
          </>
        )}
      </SheetBody>
    );
  }
  if (run.callKey !== null && run.picks) return callPanel(run.callKey, run.picks);
  if (asked.isPending) {
    return <p className="p-4 text-sm text-muted-foreground">Getting the test ready…</p>;
  }
  if (asked.isError) {
    return (
      <p className="p-4 text-sm text-destructive">
        This test could not be prepared. Close the panel and try again.
      </p>
    );
  }
  if (inputs.length > 0) {
    return <TestInputsForm key={run.attempt} inputs={inputs} onStart={startWith} />;
  }
  if (run.callKey === null) {
    return (
      <>
        <SheetBody className="p-8 text-center">
          <p className="text-sm text-muted-foreground">
            The call starts in your browser and uses your microphone.
          </p>
        </SheetBody>
        <SheetActions>
          <SheetCancel />
          <Button onClick={start}>
            <Phone className="mr-2 h-4 w-4" />
            Start test call
          </Button>
        </SheetActions>
      </>
    );
  }
  return callPanel(run.callKey, null);
}
